import React, { useCallback, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../utils/dateUtils';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import StickyScrollTable from '../../../../components/common/StickyScrollTable';
import PreferencesDataTable from '../../../../components/common/PreferencesDataTable';
import { useGetUserGroupsQuery } from '../../../../api';
import { DROPDOWN_FIELDS, USER_DROPDOWN_FIELDS, TEXT_FIELDS, DATE_FIELDS, backendColumnMap } from '../hooks/useFeedbackFormReport';

const lowerFirst = (str) => (typeof str === 'string' && str.length > 0 ? str.charAt(0).toLowerCase() + str.slice(1) : str);

const getRawOptionsForColumn = (filterOptionsRaw, column, field) => {
    if (!filterOptionsRaw) return [];

    const filterOptions = filterOptionsRaw.options ?? filterOptionsRaw;
    if (!filterOptions) return [];

    if (Array.isArray(filterOptions)) {
        const entry = filterOptions.find((o) => o && [o.column, o.columnName, o.name, o.key, o.field].some((k) => k === column || k === field));
        if (!entry) return [];
        const list = entry.values ?? entry.options ?? entry.items ?? entry.data ?? entry.list ?? [];
        return Array.isArray(list) ? list : [];
    }

    if (typeof filterOptions === 'object') {
        const list = filterOptions[column] ?? filterOptions[field] ?? filterOptions[lowerFirst(column)] ?? [];
        return Array.isArray(list) ? list : [];
    }

    return [];
};

const normalizeOptions = (rawList) => {
    if (!Array.isArray(rawList)) return [];
    return rawList
        .filter((opt) => opt !== null && opt !== undefined && opt !== '')
        .map((opt) => {
            if (typeof opt === 'object') {
                const value = opt.value ?? opt.id ?? opt.key ?? opt.text ?? opt.name ?? opt.label;
                const rawLabel = opt.label ?? opt.text ?? opt.name ?? opt.displayName ?? value;
                return { value, label: rawLabel };
            }
            return { value: opt, label: opt };
        });
};

const formatText = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    return value;
};

const FeedbackFormReportTable = ({ items, totalCount, loading, lazyParams, onPage, activeFilters, onFiltersChange, filterOptions, sortField, sortOrder, onSortChange }) => {
    const { th } = useLanguage();

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const { data: userGroupsResponse } = useGetUserGroupsQuery();

    const userOptions = useMemo(() => {
        const users = Array.isArray(userGroupsResponse?.data) ? userGroupsResponse.data : [];
        const uniqueNames = [...new Set(users.map((user) => user?.name).filter((name) => typeof name === 'string' && name.trim() !== ''))];
        return uniqueNames.map((name) => ({ value: name, label: name }));
    }, [userGroupsResponse]);

    const dropdownOptionsByField = useMemo(() => {
        const result = {};
        DROPDOWN_FIELDS.forEach((field) => {
            result[field] = USER_DROPDOWN_FIELDS.includes(field) ? userOptions : normalizeOptions(getRawOptionsForColumn(filterOptions, backendColumnMap[field], field));
        });
        return result;
    }, [filterOptions, userOptions]);

    const renderDropdownFilter = useCallback(
        (field, options) => (
            <Dropdown
                value={options.value}
                options={dropdownOptionsByField[field] || []}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder={th('common.all')}
                showClear
                filter
                filterPlaceholder={th('common.search')}
                className="p-column-filter"
                style={{ minWidth: '12rem' }}
            />
        ),
        [dropdownOptionsByField, th]
    );

    const renderDateFilter = useCallback(
        (options) => {
            const handleDateChange = (e) => {
                let d = e.value;
                if (d instanceof Date) d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
                options.filterApplyCallback(d);
            };
            return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon showButtonBar className="p-column-filter" style={{ minWidth: '12rem' }} />;
        },
        [th]
    );

    const makeTextColumn = (field, headerKey, options = {}) => {
        const { body, sortable = true, minWidth = '160px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body: body || ((row) => formatText(row?.[field])),
            filter: true,
            filterField: field,
            filterPlaceholder: th('common.search'),
            showFilterMenu: true,
            filterMatchModeOptions: textMatchModeOptions,
            showClearButton: false
        };
    };

    const makeDropdownColumn = (field, headerKey, options = {}) => {
        const { body, sortable = true, minWidth = '200px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body: body || ((row) => formatText(row?.[field])),
            filter: true,
            filterField: field,
            showFilterMenu: false,
            filterElement: (opts) => renderDropdownFilter(field, opts)
        };
    };

    const makeDateColumn = (field, headerKey, options = {}) => {
        const { sortable = true, minWidth = '170px' } = options;
        return {
            field,
            columnKey: field,
            sortable,
            header: th(headerKey),
            style: { minWidth },
            body: (row) => (row?.[field] ? formatDateOnly(row[field]) : '-'),
            filter: true,
            filterField: field,
            showFilterMenu: false,
            filterElement: renderDateFilter
        };
    };

    const tableFilters = useMemo(() => {
        const filters = {};
        TEXT_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS
            };
        });
        DROPDOWN_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: FilterMatchMode.EQUALS
            };
        });
        DATE_FIELDS.forEach((field) => {
            filters[field] = {
                value: activeFilters?.[field] ?? null,
                matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.DATE_IS
            };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = useCallback(
        (e) => {
            const filters = e?.filters || {};
            const updated = {};

            TEXT_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
                }
            });
            DROPDOWN_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                }
            });
            DATE_FIELDS.forEach((field) => {
                if (field in filters) {
                    updated[field] = filters[field]?.value ?? null;
                    updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.DATE_IS;
                }
            });

            onFiltersChange?.(updated);
        },
        [onFiltersChange]
    );

    const rows = useMemo(() => (Array.isArray(items) ? items.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` })) : []), [items, lazyParams.first]);

    const columns = [
        makeTextColumn('technicalSupportId', 'feedbackFormReport.columns.technicalSupportId', { minWidth: '130px' }),
        makeDropdownColumn('dealerName', 'feedbackFormReport.columns.dealerName', {
            minWidth: '250px',
            body: (row) => createTruncatedTextForTable(String(formatText(row?.dealerName)), 30)
        }),
        makeDropdownColumn('technicalResponsible', 'feedbackFormReport.columns.technicalResponsible', { minWidth: '190px' }),
        makeDropdownColumn('question', 'feedbackFormReport.columns.question', {
            minWidth: '250px',
            body: (row) => createTruncatedTextForTable(String(formatText(row?.question)), 30)
        }),
        makeDropdownColumn('rating', 'feedbackFormReport.columns.rating', { minWidth: '130px' }),
        makeTextColumn('comment', 'feedbackFormReport.columns.comment', {
            minWidth: '250px',
            body: (row) => createTruncatedTextForTable(String(formatText(row?.comment)), 30)
        }),
        makeDateColumn('answerDate', 'feedbackFormReport.columns.answerDate')
    ];

    return (
        <StickyScrollTable deps={[items, totalCount, loading, activeFilters]} scrollBarPosition="top">
            <PreferencesDataTable
                tableKey="technical-support-feedback-form-report"
                columns={columns}
                value={rows}
                totalRecords={totalCount}
                className="p-datatable-gridlines"
                showGridlines
                lazy
                paginator
                first={lazyParams.first}
                rows={lazyParams.rows || 10}
                onPage={onPage}
                dataKey="_rowId"
                loading={loading}
                filterDisplay="row"
                filters={tableFilters}
                onFilter={handleTableFilter}
                emptyMessage={th('common.noRecords')}
                rowsPerPageOptions={[10, 20, 50, 100]}
                tableStyle={{ minWidth: '80rem' }}
                onSort={onSortChange}
                sortField={sortField}
                sortOrder={sortOrder}
                sortMode="single"
                pt={{ wrapper: { style: { overflowX: 'scroll' } } }}
            />
        </StickyScrollTable>
    );
};

export default FeedbackFormReportTable;
